import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, Receipt, MapPin, Clock, ArrowRight, AlertTriangle, Sparkles, RefreshCw, CheckCircle2, Plus } from "lucide-react";
import { api, formatBRL, formatDateBR, formatDateTimeBR } from "../../lib/api";
import { hourBankName, economiaObtida } from "../../lib/hourbank";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    confirmed: ["bg-emerald-100 text-emerald-700", "Confirmada"],
    pending_payment: ["bg-amber-100 text-amber-700", "Aguardando pagamento"],
    expired: ["bg-red-100 text-red-700", "Expirada"],
    cancelled: ["bg-gray-100 text-gray-600", "Cancelada"],
    completed: ["bg-blue-100 text-blue-700", "Concluída"],
  };
  const [cls, label] = map[status] || ["bg-gray-100 text-gray-600", status];
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${cls}`}>{label}</span>;
}

export default function ClientDashboard() {
  const nav = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [renewing, setRenewing] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/reservations").then(r => setReservations(r.data)).catch(() => {}),
      api.get("/invoices").then(r => setInvoices(r.data)).catch(() => {}),
      api.get("/me/packages").then(r => setPackages(r.data)).catch(() => setPackages([])),
      api.get("/public/plans").then(r => setPlans(r.data)).catch(() => {}),
      api.get("/public/spaces").then(r => setSpaces(r.data)).catch(() => {}),
    ]);
  }, []);

  const renew = async (pkg) => {
    if (renewing) return;
    setRenewing(pkg.id);
    try {
      const { data } = await api.post("/packages/purchase", { plan_id: pkg.plan_id, method: "pix" });
      toast.success("Cobrança criada! Abrindo o pagamento…");
      if (data.payment_link) window.open(data.payment_link, "_blank");
      nav("/portal/faturas");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Não foi possível renovar agora."));
    } finally { setRenewing(null); }
  };

  const nextReservation = useMemo(() => {
    return reservations
      .filter(r => r.status === "confirmed" && new Date(r.start_time) > new Date())
      .sort((a, b) => a.start_time.localeCompare(b.start_time))[0] || null;
  }, [reservations]);

  const pastReservations = useMemo(() => {
    return reservations
      .filter(r => new Date(r.start_time) <= new Date() || r.status !== "confirmed")
      .sort((a, b) => b.start_time.localeCompare(a.start_time))
      .slice(0, 6);
  }, [reservations]);

  const pendingInvoices = invoices.filter(i => i.status !== "paid");
  const activePackages = packages.filter(p => p.status === "active");
  const { user } = useAuth();

  // Smart upsell rule: show package promo if customer has 2+ reservations in last 30 days
  // and no active package
  const reservationsLast30 = useMemo(() => {
    const d30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return reservations.filter(r => new Date(r.created_at || r.start_time).getTime() > d30 && r.status === "confirmed").length;
  }, [reservations]);

  return (
    <div data-testid="client-dashboard" className="space-y-6">
      {/* Header — RC7 hero personalizado */}
      <div className="bg-[#0A1128] rounded-2xl p-6 sm:p-8 relative overflow-hidden" data-testid="portal-hero">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, #D4AF37 0, transparent 45%)" }} />
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-1.5">Portal do Cliente</div>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">
            Olá, {(user?.name || "").split(" ")[0] || "cliente"}!
          </h1>
          <p className="text-sm text-white/60 mt-1.5">
            Bem-vindo ao VM Corporate. {activePackages.length > 0
              ? "Seu Banco de Horas está ativo — reserve em 1 clique, sem pagamento."
              : "Reserve espaços premium e economize com um Banco de Horas."}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3" data-testid="quick-actions">
        <Link
          to="/espacos"
          className="bg-[#0A1128] text-white rounded-2xl p-4 sm:p-5 flex items-center justify-between hover:bg-[#14213D] active:scale-[0.99] transition-all shadow-lg shadow-[#0A1128]/10"
          data-testid="qa-new-reservation"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70 mb-1">Reservar</div>
            <div className="font-bold text-base sm:text-lg">Nova reserva</div>
          </div>
          <Plus size={22} className="text-[#D4AF37]" />
        </Link>
        <Link
          to="/portal/faturas"
          className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex items-center justify-between hover:border-[#D4AF37] active:scale-[0.99] transition-all shadow-sm"
          data-testid="qa-payments"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Pagamentos</div>
            <div className="font-bold text-base sm:text-lg text-[#0A1128]">
              {pendingInvoices.length > 0 ? `${pendingInvoices.length} pendente${pendingInvoices.length > 1 ? "s" : ""}` : "Tudo em dia"}
            </div>
          </div>
          <Receipt size={22} className="text-[#D4AF37]" />
        </Link>
      </div>

      {/* Pending invoices banner */}
      {pendingInvoices.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="banner-pending-invoice">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-amber-900 text-sm">Você tem fatura{pendingInvoices.length > 1 ? "s" : ""} em aberto</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Total pendente: <strong>{formatBRL(pendingInvoices.reduce((s, i) => s + (i.amount || 0), 0))}</strong>
            </div>
          </div>
          <Link to="/portal/faturas" className="text-xs font-bold text-amber-900 underline shrink-0 self-center">Pagar agora →</Link>
        </div>
      )}

      {/* RC6 — Meu Banco de Horas */}
      {activePackages.length > 0 && (
        <div data-testid="my-hour-bank">
          <h2 className="font-bold text-[#0A1128] mb-3">Meu Banco de Horas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-packages-cards">
          {activePackages.map((p) => {
            const total = p.hours_total || 0;
            const used = p.hours_used || 0;
            const remaining = p.hours_remaining || 0;
            const pct = total ? Math.min(100, (used / total) * 100) : 0;
            const urgent = remaining > 0 && remaining <= total * 0.15;
            const plan = plans.find(pl => pl.id === p.plan_id);
            const eco = economiaObtida(p, plan, spaces);
            return (
              <div key={p.id} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`pkg-card-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-[#0A1128] truncate">{hourBankName(p)}</div>
                  {urgent && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Acabando</span>}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black tabular-nums text-[#0A1128]">{remaining}h</span>
                  <span className="text-xs text-gray-500">restantes de {total}h</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                  <div className={`h-full ${urgent ? "bg-amber-500" : "bg-[#D4AF37]"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-1 text-center mt-3 text-[11px]">
                  <div><div className="text-gray-400">Adquiridas</div><div className="font-bold text-[#0A1128]">{total}h</div></div>
                  <div><div className="text-gray-400">Utilizadas</div><div className="font-bold text-[#0A1128]">{used}h</div></div>
                  <div><div className="text-gray-400">Restantes</div><div className="font-bold text-emerald-700">{remaining}h</div></div>
                </div>
                <div className="text-[11px] text-gray-500 mt-2">
                  Válido até {formatDateBR(p.valid_until)}
                </div>
                {eco && (
                  <div className="text-[11px] font-bold text-emerald-700 mt-1" data-testid={`pkg-eco-${p.id}`}>
                    Economia obtida: {formatBRL(eco)} vs. preço avulso
                  </div>
                )}
                {/* RC7 — ações rápidas do card */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Link to="/portal/reservas" className="text-xs font-bold bg-[#0A1128] text-white rounded-lg py-2 text-center hover:bg-[#14213D] active:scale-[0.99] transition-all" data-testid={`pkg-reserve-${p.id}`}>
                    Reservar agora
                  </Link>
                  <Link to="/portal/comprar-pacote" className="text-xs font-bold border-2 border-[#D4AF37] text-[#7a5f10] rounded-lg py-2 text-center hover:bg-[#D4AF37]/10 active:scale-[0.99] transition-all" data-testid={`pkg-buy-more-${p.id}`}>
                    Comprar mais horas
                  </Link>
                </div>
                {urgent && (
                  <button
                    type="button"
                    onClick={() => renew(p)}
                    disabled={renewing === p.id}
                    className="mt-3 w-full text-xs font-bold bg-[#D4AF37] text-[#0A1128] rounded-lg py-2 hover:brightness-95 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-1.5"
                    data-testid={`pkg-renew-${p.id}`}
                  >
                    {renewing === p.id ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Renovar 1-clique
                  </button>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Next reservation highlight */}
      <div data-testid="next-reservation-block">
        <h2 className="font-bold text-[#0A1128] mb-3">Sua próxima reserva</h2>
        {nextReservation ? (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="bg-emerald-50 border-b border-emerald-100 px-4 py-2 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-widest">Confirmada</span>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{nextReservation.space_name?.split(" ")[0]}</div>
                <div className="font-bold text-lg text-[#0A1128]">{nextReservation.space_name}</div>
              </div>
              <div className="space-y-1.5 text-sm text-gray-700">
                <div className="flex items-center gap-2"><Calendar size={15} /> {formatDateTimeBR(nextReservation.start_time)}</div>
                <div className="flex items-center gap-2"><Clock size={15} /> Duração: {Math.round((new Date(nextReservation.end_time) - new Date(nextReservation.start_time)) / 3600000)}h</div>
                <div className="flex items-center gap-2"><MapPin size={15} /> R. Sebastião Lacerda — Piabetá</div>
              </div>
              <Link to="/portal/reservas" className="inline-flex items-center gap-1 text-sm font-bold text-[#D4AF37] hover:gap-2 transition-all" data-testid="see-all-reservations">
                Ver detalhes <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-6 text-center">
            <Calendar size={28} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 text-sm">Você não tem nenhuma reserva futura.</p>
            <Link to="/espacos" className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#D4AF37] hover:gap-2 transition-all">
              Fazer uma reserva <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>

      {/* RC7 — destaque quando não possui Banco de Horas */}
      {activePackages.length === 0 && (
        <div className="bg-gradient-to-br from-[#D4AF37]/15 to-[#D4AF37]/5 rounded-2xl p-5 border-2 border-[#D4AF37]/40" data-testid="upsell-package">
          <div className="flex items-start gap-3">
            <Sparkles size={24} className="text-[#D4AF37] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#7a5f10] text-base">Economize comprando um Banco de Horas</div>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed">
                {reservationsLast30 >= 2
                  ? `Você fez ${reservationsLast30} reservas nos últimos 30 dias. Com um Banco de Horas suas reservas são confirmadas na hora, sem PIX — e o valor por hora cai muito.`
                  : "Reservas confirmadas na hora, sem PIX, com valor por hora muito menor que o avulso."}
              </p>
              <Link
                to="/portal/comprar-pacote"
                className="inline-flex items-center gap-1.5 mt-3 bg-[#D4AF37] text-[#0A1128] font-bold text-sm px-4 py-2 rounded-lg hover:brightness-95 active:scale-[0.99] transition-all"
                data-testid="upsell-cta"
              >
                Conhecer planos <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* History (last 6) */}
      <div data-testid="history-block">
        <h2 className="font-bold text-[#0A1128] mb-3">Histórico recente</h2>
        {pastReservations.length > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {pastReservations.map((r, i) => (
              <Link
                key={r.id}
                to="/portal/reservas"
                className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${i > 0 ? "border-t border-gray-100" : ""}`}
                data-testid={`history-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0A1128] truncate text-sm">{r.space_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {formatDateTimeBR(r.start_time)} · {formatBRL(r.price || 0)}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-gray-400 text-sm text-center py-6">Nenhuma reserva anterior.</div>
        )}
      </div>
    </div>
  );
}
